import React, { useState, useRef } from 'react';

interface PlayerSetupProps {
  players: string[];
  onAddPlayer: (name: string) => boolean;
  onRemovePlayer: (index: number) => void;
  canAddMore: boolean;
}

export default function PlayerSetup({ players, onAddPlayer, onRemovePlayer, canAddMore }: PlayerSetupProps) {
  const [inputValue, setInputValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (onAddPlayer(inputValue)) {
      setInputValue('');
      inputRef.current?.focus();
    }
  };

  return (
    <div className="w-full max-w-md mx-auto">
      {/* Input form */}
      <form onSubmit={handleSubmit} className="flex gap-3 mb-6">
        <input
          ref={inputRef}
          type="text"
          value={inputValue}
          onChange={e => setInputValue(e.target.value)}
          placeholder="Prénom du joueur..."
          maxLength={20}
          disabled={!canAddMore}
          className="flex-1 px-5 py-3.5 rounded-2xl bg-white/10 border border-white/20 text-white placeholder-white/40 font-body text-base outline-none focus:border-white/40 focus:bg-white/15 transition-all"
        />
        <button
          type="submit"
          disabled={!canAddMore || !inputValue.trim()}
          className="px-5 py-3.5 rounded-2xl bg-gradient-to-r from-blue-500 to-blue-600 text-white font-display font-bold text-xl btn-press disabled:opacity-30 transition-opacity"
        >
          +
        </button>
      </form>

      {/* Players count */}
      <div className="flex items-center justify-between mb-3 px-1">
        <span className="text-white/40 font-body text-sm">
          {players.length} joueur{players.length !== 1 ? 's' : ''}
        </span>
        <span className="text-white/30 font-body text-xs">
          Max. 10
        </span>
      </div>

      {/* Players list */}
      <div className="space-y-2 max-h-[340px] overflow-y-auto pr-1">
        {players.map((player, index) => (
          <div
            key={`${player}-${index}`}
            className="flex items-center justify-between px-5 py-3.5 rounded-2xl bg-white/8 border border-white/10 animate-slide-up"
            style={{ animationDelay: `${index * 50}ms` }}
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 rounded-full bg-gradient-to-br from-blue-400 to-purple-500 flex items-center justify-center font-display font-bold text-sm text-white">
                {player.charAt(0).toUpperCase()}
              </div>
              <span className="font-body font-medium text-white text-base">
                {player}
              </span>
            </div>
            <button
              onClick={() => onRemovePlayer(index)}
              className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center text-white/50 hover:bg-red-500/30 hover:text-red-400 transition-all btn-press"
            >
              ✕
            </button>
          </div>
        ))}

        {players.length === 0 && (
          <div className="text-center py-10">
            <p className="text-white/20 font-body text-sm">
              Ajoute au moins 2 joueurs pour commencer
            </p>
          </div>
        )}
      </div>
    </div>
  );
}
